type InfoIcon = {
  body?: string;
  bodyHigh?: string;
  bodyLow?: string;
  button?: string;
};

type TemplateConfig = {
  pages?: {
    vmo?: {
      vmoInfo?: {
        [key: string]: {
          infoIcon?: InfoIcon;
        };
      };
    };
  };
};

export const HandleModal = (label: string, templateConfig: TemplateConfig, loanApplication?: any): InfoIcon => {
  const labelCapitalized = label.toUpperCase();
  const labelMapping: Record<string, string> = {
    'LINE AMOUNT': 'lineAmount',
    'LOAN AMOUNT': 'loanAmount',
    'ESTIMATED APR': 'estimatedAPR',
    'ESTIMATED MONTHLY PAYMENT': 'estimatedMtPayment',
    'EST MONTHLY PMT': 'estimatedMtPayment',
    'ORIGINATION FEE': 'originationFee',
    'INITIAL DRAW': 'initialDraw',
    'MINIMUM DRAW': 'minimumDraw',
    'MAXIMUM DRAW': 'maximumDraw',
  };

  const drawKeys = ['initialDraw', 'minimumDraw', 'maximumDraw'];

  const formatModalMessage = (messageTemplate: string | undefined): string => {
    if (!messageTemplate || !loanApplication) return messageTemplate || '';

    const drawPercentage = loanApplication?.availablePrograms?.helocData?.rates[0]?.minDrawPercentage ?? loanApplication?.helocData?.minDrawPercentage ?? 0;

    const replaced = messageTemplate
      .replace(/%%minLineAmount%%/g, `${loanApplication?.minLineAmount?.toLocaleString() || '0'}`)
      .replace(/%%maxLineAmount%%/g, `${loanApplication?.maxLineAmount?.toLocaleString() || '0'}`)
      .replace(/%%minLoanAmount%%/g, `${loanApplication?.availablePrograms?.helRates?.[0]?.minLoanAmount?.toLocaleString() || '0'}`)
      .replace(/%%maxLoanAmount%%/g, `${loanApplication?.availablePrograms?.helRates?.[0]?.maxLoanAmount?.toLocaleString() || '0'}`)
      .replace(/%%DrawPercentage%%/g, `${drawPercentage}`)
      .replace(/%%minDrawAmount%%/g, `${loanApplication?.selectedProgram?.minDrawAmount?.toLocaleString() || '0'}`)
      .replace(/%%maxDrawAmount%%/g, `${loanApplication?.selectedProgram?.maxDrawAmount?.toLocaleString() || '0'}`)
      .replace(/%%initialLineAmount%%/g, `${loanApplication?.initialLineAmount?.toLocaleString() || '0'}`)
      .replace(/%%apr%%/g, `${loanApplication?.apr || '0'}%`)
      .replace(/%%estimatedMonthlyPayment%%/g, `${loanApplication?.estimatedMonthlyPayment?.toLocaleString() || '0'}`);
    return replaced;
  };

  const getModalBody = (modalObj: any, isDrawType: boolean = false, isOriginationFee: boolean = false): string => {
    const ratesMinHeloc = Number(loanApplication?.availablePrograms?.helocData?.rates?.[0]?.minLineAmount);
    const ratesMinHel = Number(loanApplication?.availablePrograms?.helRates?.[0]?.minLineAmount);
    const stateMin = Number(loanApplication?.minLineAmount);
    const selectedMin = Number(loanApplication?.selectedProgram?.minLineAmount);
    const initialMin = Number(loanApplication?.initialLineAmount);
    const decisionMinLineAmount =
      !Number.isNaN(ratesMinHeloc) && ratesMinHeloc > 0
        ? ratesMinHeloc
        : !Number.isNaN(ratesMinHel) && ratesMinHel > 0
          ? ratesMinHel
          : !Number.isNaN(stateMin) && stateMin > 0
            ? stateMin
            : !Number.isNaN(selectedMin) && selectedMin > 0
              ? selectedMin
              : !Number.isNaN(initialMin) && initialMin > 0
                ? initialMin
                : 0;

    if (isOriginationFee && typeof modalObj === 'string') {
      return formatModalMessage(modalObj);
    }
    if (!modalObj || typeof modalObj !== 'object') return '';

    if (isDrawType) {
      const isHighLineAmount = decisionMinLineAmount >= 50000;
      const lineAmountKey = isHighLineAmount ? 'highLineAmount' : 'lowLineAmount';
      const message = modalObj[lineAmountKey] || '';
      return formatModalMessage(message);
    }

    const entries = Object.entries(modalObj);
    const highEntry = entries.find(([key]) => key.toLowerCase().includes('high'));
    const lowEntry = entries.find(([key]) => key.toLowerCase().includes('low'));

    if (highEntry && lowEntry) {
      const isHigh = decisionMinLineAmount >= 50000;
      let selectedMessage = isHigh ? (highEntry[1] as string) : (lowEntry[1] as string);

      return formatModalMessage(selectedMessage);
    }

    if (modalObj.bodyHigh && modalObj.bodyLow) {
      const isHigh = decisionMinLineAmount >= 50000;
      let selectedMessage = isHigh ? modalObj.bodyHigh : modalObj.bodyLow;
      return formatModalMessage(selectedMessage);
    }

    const message = modalObj.body || Object.values(modalObj).find((val) => typeof val === 'string') || '';
    return formatModalMessage(message);
  };

  const key = labelMapping[labelCapitalized];
  const isDrawType = drawKeys.includes(key);
  const isOriginationFee = key === 'originationFee';

  let infoIcon;
  if (isDrawType) {
    infoIcon = key ? (templateConfig as any)?.pages?.vmo?.vmoInfo?.drawAmount?.[key] : null;
  } else {
    infoIcon = key ? (templateConfig as any)?.pages?.vmo?.vmoInfo?.[key] : null;
  }

  const result = infoIcon ?? { bodyHigh: '', bodyLow: '', button: 'Close' };

  let formattedBody;
  if (key === 'loanAmount') {
    const message = typeof result === 'string' ? result : result.body || '';
    formattedBody = formatModalMessage(message);
  } else {
    formattedBody = getModalBody(result, isDrawType, isOriginationFee);
  }

  return {
    body: formattedBody,
    button: (typeof result === 'object' && result.button) || 'Close',
  };
};
